import { action, makeObservable, observable } from 'mobx';
import { enumKeys } from '@/helpers/utils';

export enum Locale {
  en = 'en',
  de = 'de',
}

type RawLocaleMessages = Array<[string, string | RawLocaleMessages]>;
export type LocaleMessages = Record<string, string>;
type LocaleMap = Map<string, string>;

type LocaleSwitchListener = (locale: Locale) => void;

class Localization {
  locale: Locale;
  /**
   * If locale is controlled by host site.
   *
   * @see EmbeddingService.subscribeToLocaleChange
   */
  externalControl: boolean = false;

  private localeSwitchListener?: LocaleSwitchListener;

  private localeMessages: LocaleMap;

  constructor(messages: LocaleMessages, initialLocale: Locale) {
    this.locale = initialLocale;

    makeObservable(this, {
      locale: observable,
      externalControl: observable,
      init: action.bound,
      setLocaleSwitchListener: action.bound,
      removeLocaleSwitchListener: action.bound,
      changeLanguage: action.bound,
    });

    this.localeMessages = Localization.constructLocaleMap(messages);
  }

  async init() {}

  private static constructLocaleMap(messages: LocaleMessages): LocaleMap {
    const result = new Map<string, string>();
    for (let [key, value] of Object.entries(messages)) {
      result.set(key, value);
    }

    return result;
  }

  setLocaleSwitchListener(listener: LocaleSwitchListener) {
    this.localeSwitchListener = listener;
  }

  removeLocaleSwitchListener() {
    this.localeSwitchListener = undefined;
  }

  async changeLanguage(locale: Locale) {
    this.locale = locale;
    await this.init();

    if (this.localeSwitchListener) {
      this.localeSwitchListener(locale);
    }
  }

  static isLocaleSupported(locale: any): locale is keyof typeof Locale {
    return enumKeys(Locale).includes(locale);
  }

  static get defaultLocale() {
    return Locale.en;
  }

  static readonly KEY_LOCALE = 'LOCALE';

  static get BrowserAwareLocale() {
    const mainLocale = navigator.language.substring(0, 2);
    return this.isLocaleSupported(mainLocale) ? Locale[mainLocale] : Localization.defaultLocale;
  }

  static get PreferredUserLocale() {
    let storedLocale = localStorage.getItem(Localization.KEY_LOCALE);

    return this.isLocaleSupported(storedLocale) ? Locale[storedLocale] : Localization.BrowserAwareLocale;
  }
}

export default Localization;
